/**
 * Figma-style edge attachment. By default an edge is not anchored to a fixed
 * handle: at render time each end picks the side of its node that faces the
 * other end (the dominant axis of the centre-to-centre vector) and anchors at
 * that side's midpoint, where the real `<Handle>` sits. So the attachment
 * snaps between the four sides as nodes move, rather than sliding around the
 * shape's perimeter.
 *
 * A side can be pinned. A pinned end stays on its side through every move;
 * the free end then aims at the pinned anchor rather than the node centre, so
 * the line still leaves the free node on the side that faces where it goes.
 *
 * The dominant-axis choice mirrors the backend's `bestEdgeHandles`
 * (`shapeHandle` in canvas_edit.go) so agent- and client-routed edges agree.
 * Purely a render-time computation: nothing is written while nodes move.
 */

import { type InternalNode, type Node, Position } from "@xyflow/react";

export interface FloatingEdgeParams {
  sx: number;
  sy: number;
  tx: number;
  ty: number;
  sourcePos: Position;
  targetPos: Position;
}

export interface FloatingEdgePins {
  sourceSide?: Position | null;
  targetSide?: Position | null;
}

/** Bounding-box geometry pulled from an internal node, or null when unmeasured. */
interface NodeRect {
  x: number;
  y: number;
  w: number;
  h: number;
  cx: number;
  cy: number;
}

function nodeRect(node: InternalNode<Node>): NodeRect | null {
  const w = node.measured?.width ?? node.width ?? undefined;
  const h = node.measured?.height ?? node.height ?? undefined;
  const pos = node.internals.positionAbsolute;
  if (
    w == null ||
    h == null ||
    !Number.isFinite(w) ||
    !Number.isFinite(h) ||
    w <= 0 ||
    h <= 0 ||
    !pos ||
    !Number.isFinite(pos.x) ||
    !Number.isFinite(pos.y)
  ) {
    return null;
  }
  return { x: pos.x, y: pos.y, w, h, cx: pos.x + w / 2, cy: pos.y + h / 2 };
}

/**
 * The side of `from` facing the point `to`, chosen by the dominant axis of
 * the centre-to-point vector. Mirrors `shapeHandle` in canvas_edit.go:
 * horizontal dominance → right/left, vertical dominance → bottom/top (ties
 * favour horizontal, matching `>=`).
 */
export function sideFacing(
  from: NodeRect,
  to: { x: number; y: number },
): Position {
  const dx = to.x - from.cx;
  const dy = to.y - from.cy;
  if (Math.abs(dx) >= Math.abs(dy)) {
    return dx >= 0 ? Position.Right : Position.Left;
  }
  return dy >= 0 ? Position.Bottom : Position.Top;
}

/** Midpoint of `rect`'s `side` — where that side's `<Handle>` is anchored. */
export function sideMidpoint(
  rect: NodeRect,
  side: Position,
): { x: number; y: number } {
  switch (side) {
    case Position.Left:
      return { x: rect.x, y: rect.cy };
    case Position.Right:
      return { x: rect.x + rect.w, y: rect.cy };
    case Position.Top:
      return { x: rect.cx, y: rect.y };
    case Position.Bottom:
      return { x: rect.cx, y: rect.y + rect.h };
  }
}

/** The side of a measured node nearest to a point (for dropping an edge end on it). */
export function nearestSide(
  node: InternalNode<Node>,
  point: { x: number; y: number },
): Position | null {
  const r = nodeRect(node);
  if (!r) return null;
  // Normalise the offset from centre by the half-size so a wide node does
  // not always answer left/right.
  const nx = (point.x - r.cx) / (r.w / 2);
  const ny = (point.y - r.cy) / (r.h / 2);
  if (Math.abs(nx) >= Math.abs(ny)) {
    return nx >= 0 ? Position.Right : Position.Left;
  }
  return ny >= 0 ? Position.Bottom : Position.Top;
}

/**
 * Compute the side pair + their midpoint anchors for an edge between two
 * nodes, honouring pinned sides. Returns null when either node isn't measured
 * yet (caller should fall back to React Flow's handle-derived coordinates).
 */
export function getFloatingEdgeParams(
  source: InternalNode<Node> | undefined,
  target: InternalNode<Node> | undefined,
  pins: FloatingEdgePins = {},
): FloatingEdgeParams | null {
  if (!source || !target) return null;
  const s = nodeRect(source);
  const t = nodeRect(target);
  if (!s || !t) return null;
  const sourcePin = pins.sourceSide ?? null;
  const targetPin = pins.targetSide ?? null;
  // Fully overlapping centres have no meaningful direction; bail to the
  // handle-based fallback rather than picking an arbitrary side.
  if (!sourcePin && !targetPin && s.cx === t.cx && s.cy === t.cy) return null;

  let sourcePos = sourcePin ?? sideFacing(s, { x: t.cx, y: t.cy });
  let targetPos = targetPin ?? sideFacing(t, { x: s.cx, y: s.cy });
  if (sourcePin && !targetPin) {
    targetPos = sideFacing(t, sideMidpoint(s, sourcePin));
  } else if (targetPin && !sourcePin) {
    sourcePos = sideFacing(s, sideMidpoint(t, targetPin));
  }
  const sp = sideMidpoint(s, sourcePos);
  const tp = sideMidpoint(t, targetPos);
  return {
    sx: sp.x,
    sy: sp.y,
    tx: tp.x,
    ty: tp.y,
    sourcePos,
    targetPos,
  };
}

/** A stored pinned side (`source_side` / `target_side`), or null when absent or malformed. */
export function sideFromData(v: unknown): Position | null {
  return v === "top" || v === "right" || v === "bottom" || v === "left"
    ? (v as Position)
    : null;
}
